import { ITransaction } from '@/types'

// The transaction fields offered as a pick list. Each is a small set per customer -- a handful of
// institutions, accounts and class types -- which is what makes a dropdown the right control.
// label names the set, so a control with nothing picked can read "All institutions" and one with
// picks can read "Institutions 2". Status is left out: it does not help find a transaction.
export const FILTER_FIELDS = [
  { key: 'institutionName', label: 'Institutions' },
  { key: 'name', label: 'Account names' },
  { key: 'accountNumber', label: 'Account numbers' },
  { key: 'accountClass', label: 'Class types' },
] as const

export type FilterField = (typeof FILTER_FIELDS)[number]['key']

// What one field lets through. Values picked from a blank slate keep only those; values unticked
// from the whole set leave just those out, so a value the rows read later have only now turned up
// is still let through. Leaving nothing out is the whole set.
export interface IFieldSelection {
  mode: 'only' | 'except'
  values: string[]
}

export const ALL_VALUES: IFieldSelection = { mode: 'except', values: [] }

export const isAllValues = (selection: IFieldSelection) =>
  selection.mode === 'except' && selection.values.length === 0

export const letsThrough = (selection: IFieldSelection, value: string) =>
  selection.mode === 'only' ? selection.values.includes(value) : !selection.values.includes(value)

export interface ITransactionFilters {
  selections: Record<FilterField, IFieldSelection>
  // Held as typed rather than as a number so a half-entered value does not read as zero. Both
  // bounds are inclusive, so a floor of 100 keeps a transaction of exactly 100.
  minAmount: string
  maxAmount: string
}

export const EMPTY_FILTERS: ITransactionFilters = {
  selections: {
    institutionName: ALL_VALUES,
    name: ALL_VALUES,
    accountNumber: ALL_VALUES,
    accountClass: ALL_VALUES,
  },
  minAmount: '',
  maxAmount: '',
}

// An unparseable or empty entry means no bound rather than a bound of zero.
const isAmountBound = (value: string) => value.trim() !== '' && !Number.isNaN(Number(value))

// A floor above the ceiling can match nothing. It is flagged on the fields and left unapplied, so
// the table is not emptied by a range that was mistyped.
export const isAmountRangeInvalid = (filters: ITransactionFilters) =>
  isAmountBound(filters.minAmount) &&
  isAmountBound(filters.maxAmount) &&
  Number(filters.minAmount) > Number(filters.maxAmount)

// Either bound narrows by amount, which is what lets rows be picked for a bulk alias.
export const hasAmountFilter = (filters: ITransactionFilters) =>
  !isAmountRangeInvalid(filters) &&
  (isAmountBound(filters.minAmount) || isAmountBound(filters.maxAmount))

// Whether the filters actually narrow the rows: a pick, or an amount that can be applied.
export const narrowsRows = (filters: ITransactionFilters) =>
  hasAmountFilter(filters) ||
  FILTER_FIELDS.some(field => !isAllValues(filters.selections[field.key]))

// Counts anything typed, even an unparseable amount, so Clear filters is offered to remove it.
export const hasActiveFilters = (filters: ITransactionFilters) =>
  filters.minAmount.trim() !== '' ||
  filters.maxAmount.trim() !== '' ||
  FILTER_FIELDS.some(field => !isAllValues(filters.selections[field.key]))

// The values on offer are the ones present in the rows read so far, so a pick can never return
// nothing. Sorted so the list does not reshuffle as more rows arrive.
export const filterOptions = (
  rows: ITransaction[],
  // Values known independently of the rows read -- the customer's connections and accounts -- so a
  // filter can offer an account the loaded transactions have not reached yet. Merged rather than
  // substituted, because a transaction can name an account the directory no longer lists.
  known: Partial<Record<FilterField, string[]>> = {}
): Record<FilterField, string[]> => {
  const options = {} as Record<FilterField, string[]>

  for (const field of FILTER_FIELDS) {
    const values = new Set<string>()

    for (const value of known[field.key] ?? []) {
      if (value?.trim()) {
        values.add(value)
      }
    }

    for (const row of rows) {
      const value = row[field.key]
      if (typeof value === 'string' && value.trim() !== '') {
        values.add(value)
      }
    }

    options[field.key] = [...values].sort((left, right) => left.localeCompare(right))
  }

  return options
}

export const applyFilters = (rows: ITransaction[], filters: ITransactionFilters) => {
  const minAmount = Number(filters.minAmount)
  const maxAmount = Number(filters.maxAmount)
  const appliesAmount = hasAmountFilter(filters)
  const hasMinAmount = appliesAmount && isAmountBound(filters.minAmount)
  const hasMaxAmount = appliesAmount && isAmountBound(filters.maxAmount)

  return rows.filter(row => {
    const amount = Number(row.amount)
    // Written as negations so an amount that does not parse fails a bound rather than passing it.
    if (hasMinAmount && !(amount >= minAmount)) {
      return false
    }
    if (hasMaxAmount && !(amount <= maxAmount)) {
      return false
    }

    return FILTER_FIELDS.every(field =>
      letsThrough(filters.selections[field.key], String(row[field.key]))
    )
  })
}
